import React, { useEffect } from "react";
import { API, HUNTER_KEY, inferWebsiteFromOpportunityItem } from "../appShared";
import { FaChartBar, FaSpinner } from "react-icons/fa6";
import { FaEnvelope, FaPhone, FaGlobe, FaLinkedin, FaSearch, FaCaretUp, FaClipboard } from "react-icons/fa";
export default function OutreachTab({ lead, updateLead, crawlEmail, crawlProjectDetails, generateOutreach, outreachLoading, outreachMsg, setOutreachMsg, gmailStatus, sendEmail, findEmail, searching }) {
  useEffect(() => { setOutreachMsg(lead.outreachMsg || ""); }, [lead.id, lead.outreachMsg, setOutreachMsg]);

  const [emailSubject, setEmailSubject]     = React.useState("");
  const [manualEmail, setManualEmail]       = React.useState("");
  const [manualWebsite, setManualWebsite]   = React.useState("");
  const [manualPhone, setManualPhone]       = React.useState("");
  const [manualLinkedin, setManualLinkedin] = React.useState("");
  const [emailBody, setEmailBody]           = React.useState(outreachMsg || "");
  const [selectedTone, setSelectedTone]     = React.useState("Friendly");
  const [finderOpen, setFinderOpen]         = React.useState(false);

  // Contact signals (use discovered project contacts as fallback)
  const primaryEmail = lead.email || lead.projectContactEmails?.[0] || "";
  const primaryPhone = lead.phone || lead.projectContactPhones?.[0] || "";
  const hasEmail    = !!primaryEmail;
  const hasPhone    = !!primaryPhone;
  const hasLinkedin = !!lead.linkedin;
  const hasWebsite  = (lead.websiteStatus === "found") || !!lead.website;
  const hasAnyReachPath = hasEmail || hasPhone || hasLinkedin || hasWebsite;
  const requireEmailFirst = !hasEmail;
  const contactScore = [hasEmail, hasPhone, hasLinkedin, hasWebsite].filter(Boolean).length;
  const reachPct = Math.round((contactScore / 4) * 100);

  const inferredFromSource = !lead.website
    ? inferWebsiteFromOpportunityItem({ link: lead.sourceUrl, title: lead.name, description: lead.description })
    : null;

  const leadDomain = (() => {
    if (lead.websiteStatus === "missing") return null;
    try { return new URL(lead.website.startsWith("http") ? lead.website : "https://" + lead.website).hostname.replace("www.", ""); } catch { return null; }
  })();

  // 5 proper channels
  const CHANNELS = [
    { id: "email",   icon: <FaEnvelope />,  label: "Email",        ready: hasEmail,    status: hasEmail ? primaryEmail : "No email",          action: "Send via Gmail" },
    { id: "whatsapp",icon: <FaChartBar />,  label: "WhatsApp",     ready: hasPhone,    status: hasPhone ? primaryPhone : "No phone",          action: "Copy & send" },
    { id: "linkedin",icon: <FaLinkedin />,  label: "LinkedIn DM",  ready: hasLinkedin, status: hasLinkedin ? "Profile found" : "Not found", action: hasLinkedin ? "Open & DM" : "Find & DM" },
    { id: "phone",   icon: <FaPhone />,     label: "Phone Call",   ready: hasPhone,    status: hasPhone ? primaryPhone : "No phone",          action: "Call" },
    { id: "form",    icon: <FaClipboard />, label: "Contact Form", ready: hasWebsite,  status: hasWebsite ? leadDomain : "No website",      action: "Open form" },
  ];
  const [selectedChannel, setSelectedChannel] = React.useState("email");
  const activeChannel = CHANNELS.find(c => c.id === selectedChannel) || CHANNELS[0];

  React.useEffect(() => {
    if (outreachMsg) {
      const lines = outreachMsg.split("\n");
      const subLine = lines.find(l => l.toLowerCase().startsWith("subject:"));
      if (subLine) {
        setEmailSubject(subLine.replace(/^subject:/i, "").trim());
        setEmailBody(lines.filter(l => !l.toLowerCase().startsWith("subject:")).join("\n").trim());
      } else {
        setEmailSubject(`Quick note about ${lead.name}`);
        setEmailBody(outreachMsg);
      }
    } else {
      setEmailSubject(`Quick note about ${lead.name}`);
      setEmailBody("");
    }
  }, [outreachMsg, lead.name]);

  const tones = ["Friendly", "Direct", "Premium"];
  const draftText  = emailBody || outreachMsg || "";
  const wordCount  = draftText.trim() ? draftText.trim().split(/\s+/).length : 0;
  const hasName    = lead.name ? draftText.toLowerCase().includes(lead.name.toLowerCase().split(" ")[0]) : false;
  const hasCTA     = /(call|chat|meeting|quick 15|open to|available this week|discuss)/i.test(draftText);
  const hasService = /(website|ux|marketing|automation|design|outreach|lead)/i.test(draftText);
  const isShortEnough = wordCount > 0 && wordCount <= 110;
  const qualityScore  = [hasName, hasCTA, hasService, isShortEnough].filter(Boolean).length * 25;

  const addSnippet = (snippet) => {
    const next = draftText ? `${draftText}\n\n${snippet}` : snippet;
    setEmailBody(next); setOutreachMsg(next);
  };

  const googleQ = (q) => window.open(`https://www.google.com/search?q=${encodeURIComponent(q)}`, "_blank", "noopener,noreferrer");

  // What to do when Send is clicked based on channel
  const handleSend = () => {
    const body = emailBody || outreachMsg;
    if (selectedChannel === "email") {
      if (hasEmail && gmailStatus.connected) sendEmail({ ...lead, email: primaryEmail }, emailSubject || `Quick note about ${lead.name}`, body);
      else if (hasEmail) window.open(`${API}/api/gmail/auth`, "_blank", "noopener,noreferrer");
      else setFinderOpen(true);
    } else if (selectedChannel === "whatsapp" && hasPhone) {
      window.open(`https://wa.me/${primaryPhone.replace(/\D/g, "")}?text=${encodeURIComponent(body)}`, "_blank", "noopener,noreferrer");
    } else if (selectedChannel === "linkedin") {
      navigator.clipboard.writeText(body);
      if (hasLinkedin) window.open(lead.linkedin, "_blank", "noopener,noreferrer");
      else window.open(`https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(lead.name)}`, "_blank", "noopener,noreferrer");
    } else if (selectedChannel === "phone" && hasPhone) {
      window.open(`tel:${primaryPhone}`);
    } else if (selectedChannel === "form" && hasWebsite) {
      const base = lead.website.startsWith("http") ? lead.website : "https://" + lead.website;
      window.open(base + "/contact", "_blank", "noopener,noreferrer");
    } else {
      setFinderOpen(true);
    }
  };

  const sendLabel = () => {
    if (selectedChannel === "email") return hasEmail && gmailStatus.connected ? "Send via Gmail" : hasEmail ? "Connect Gmail to Send" : "Find Email First";
    if (selectedChannel === "whatsapp") return hasPhone ? "Send via WhatsApp" : "Copy & Open WhatsApp";
    if (selectedChannel === "linkedin") return hasLinkedin ? "Copy & Open LinkedIn" : "Find on LinkedIn";
    if (selectedChannel === "phone") return hasPhone ? "Call Now" : "Find Phone Number";
    if (selectedChannel === "form") return hasWebsite ? "Open Contact Form" : "Find Website";
    return "Send";
  };

  return (
    <div className="outreach-tab" style={{ pointerEvents: searching ? "none" : "auto" }}>

      {/* ─── REACH SCORE BAR ─── */}
      <div className={`reach-bar ${contactScore === 0 ? "reach-zero" : contactScore >= 3 ? "reach-high" : "reach-mid"}`}>
        <div className="reach-bar-left" style={{ pointerEvents: searching ? "none" : "auto" }}>
          <div className="reach-bar-pct" style={{ pointerEvents: searching ? "none" : "auto" }}>{reachPct}%</div>
          <div className="reach-bar-label" style={{ pointerEvents: searching ? "none" : "auto" }}>Reach</div>
        </div>
        <div className="reach-bar-signals" style={{ pointerEvents: searching ? "none" : "auto" }}>
          <span className={`rsig ${hasEmail ? "rsig-ok" : "rsig-no"}`}><FaEnvelope style={{marginRight:4}} /> {hasEmail ? primaryEmail.split("@")[0] + "@…" : "no email"}</span>
          <span className={`rsig ${hasPhone ? "rsig-ok" : "rsig-no"}`}><FaPhone style={{marginRight:4}} /> {hasPhone ? primaryPhone : "no phone"}</span>
          <span className={`rsig ${hasLinkedin ? "rsig-ok" : "rsig-no"}`}><FaLinkedin style={{marginRight:4}} /> {hasLinkedin ? "LinkedIn" : "no LinkedIn"}</span>
          <span className={`rsig ${hasWebsite ? "rsig-ok" : "rsig-no"}`}><FaGlobe style={{marginRight:4}} /> {hasWebsite ? leadDomain || "website" : "no website"}</span>
        </div>
        <button className="reach-finder-toggle" onClick={() => setFinderOpen(o => !o)}>
          {finderOpen ? <><FaCaretUp style={{marginRight:4}} />Hide</> : <><FaSearch style={{marginRight:4}} />Find More</>}
        </button>
      </div>

      {!hasAnyReachPath && (
        <div className="outreach-empty-state" style={{ marginTop: 10 }}>
          <div className="outreach-hint" style={{ pointerEvents: searching ? "none" : "auto" }}>
            No contact path yet for this lead. You cannot send anything until you find at least one route: email, phone, LinkedIn, or website. If you do not want to work this lead now, mark it as lost.
          </div>
          <div className="outreach-actions" style={{ marginTop: 10 }}>
            <button className="btn-primary btn-sm" onClick={() => setFinderOpen(true)}><FaSearch style={{marginRight:6}} />Find contact first</button>
            <button className="btn-ghost btn-sm" onClick={() => updateLead(lead.id, { decision: "Skip", status: "Lost" })}>Mark Lost</button>
          </div>
        </div>
      )}

      {/* ─── EMAIL / CONTACT FINDER PANEL ─── */}
      {finderOpen && (
        <div className="finder-panel" style={{ pointerEvents: searching ? "none" : "auto" }}>
          <div className="finder-panel-title" style={{ pointerEvents: searching ? "none" : "auto" }}><FaSearch style={{marginRight:6}} />Maximum Contact Discovery</div>

          {/* Auto-search tools */}
          <div className="finder-section-label" style={{ pointerEvents: searching ? "none" : "auto" }}>Auto-search (API)</div>
          <div className="finder-tools" style={{ pointerEvents: searching ? "none" : "auto" }}>
            {!lead.website && inferredFromSource && (
              <button
                className="finder-tool-btn"
                style={{background:"#eef2ff", borderColor:"#a5b4fc"}}
                onClick={() => updateLead(lead.id, { website: inferredFromSource, websiteStatus: "found" })}
              >
                <span className="ftb-name">Use source domain as website ({inferredFromSource.replace(/^https?:\/\//, "")})</span>
              </button>
            )}
            {HUNTER_KEY && lead.website && (
              <button className="finder-tool-btn ftb-hunter" onClick={() => { findEmail(lead); setFinderOpen(false); }}>
                <span className="ftb-name">Search email via Hunter.io</span>
              </button>
            )}
            {lead.website && (
              <button className="finder-tool-btn ftb-hunter" style={{background:"#ecfdf5",borderColor:"#6ee7b7"}} onClick={() => { crawlEmail(lead); setFinderOpen(false); }}>
                <span className="ftb-name">Crawl website for email</span>
              </button>
            )}
            {lead.website && (
              <button className="finder-tool-btn" style={{background:"#fff7ed",borderColor:"#fdba74"}} onClick={() => { crawlProjectDetails(lead); setFinderOpen(false); }}>
                <span className="ftb-name">Extract all contact details from key pages</span>
              </button>
            )}
          </div>
          {/* Smart Google searches */}
          <div className="finder-section-label" style={{ pointerEvents: searching ? "none" : "auto" }}>Smart Google Searches (free)</div>
          <div className="finder-google-btns" style={{ pointerEvents: searching ? "none" : "auto" }}>
            <button className="finder-google-btn" onClick={() => googleQ(`"${lead.name}" contact email`)}><FaSearch style={{marginRight:6}} />"{lead.name}" contact email</button>
            <button className="finder-google-btn" onClick={() => googleQ(`"${lead.name}" site:linkedin.com`)}><FaSearch style={{marginRight:6}} />LinkedIn profile</button>
            {leadDomain && <button className="finder-google-btn" onClick={() => googleQ(`site:${leadDomain} email OR contact`)}><FaSearch style={{marginRight:6}} />Emails on their site</button>}
            {leadDomain && <button className="finder-google-btn" onClick={() => googleQ(`"@${leadDomain}" email`)}><FaSearch style={{marginRight:6}} />@{leadDomain} addresses</button>}
            <button className="finder-google-btn" onClick={() => googleQ(`"${lead.name}" "@gmail.com" OR "@outlook.com"`)}><FaSearch style={{marginRight:6}} />Personal email guess</button>
            <button className="finder-google-btn" onClick={() => googleQ(`"${lead.name}" phone number OR "contact us"`)}><FaSearch style={{marginRight:6}} />Phone number</button>
          </div>

          {/* Domain aliases */}
          {leadDomain && (
            <>
              <div className="finder-section-label" style={{ pointerEvents: searching ? "none" : "auto" }}>Common email guesses for {leadDomain}</div>
              <div className="email-alias-chips" style={{ pointerEvents: searching ? "none" : "auto" }}>
                {["info", "hello", "contact", "sales", "marketing", "admin", "support", "post"].map(prefix => (
                  <button key={prefix} className="email-alias-chip" onClick={() => { updateLead(lead.id, { email: `${prefix}@${leadDomain}` }); setFinderOpen(false); }}>
                    {prefix}@{leadDomain}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Save what you found */}
          <div className="finder-section-label" style={{ pointerEvents: searching ? "none" : "auto" }}>Save what you found</div>
          <div className="finder-save-grid" style={{ pointerEvents: searching ? "none" : "auto" }}>
            <div className="finder-save-row" style={{ pointerEvents: searching ? "none" : "auto" }}>
              <span className="finder-save-icon"><FaEnvelope /></span>
              <input className="finder-save-input" type="email" placeholder={lead.email || "Email address…"}
                value={manualEmail} onChange={e => setManualEmail(e.target.value)} />
              <button className="finder-save-btn" disabled={!manualEmail.includes("@")}
                onClick={() => { updateLead(lead.id, { email: manualEmail }); setManualEmail(""); }}>Save</button>
            </div>
            <div className="finder-save-row" style={{ pointerEvents: searching ? "none" : "auto" }}>
              <span className="finder-save-icon"><FaGlobe /></span>
              <input className="finder-save-input" type="url" placeholder={lead.website || "Website URL (e.g. akks.no)…"}
                value={manualWebsite} onChange={e => setManualWebsite(e.target.value)} />
              <button className="finder-save-btn" disabled={!manualWebsite.trim()}
                onClick={() => { updateLead(lead.id, { website: (manualWebsite.startsWith("http") ? manualWebsite : `https://${manualWebsite}`), websiteStatus: "found" }); setManualWebsite(""); }}>Save</button>
            </div>
            <div className="finder-save-row" style={{ pointerEvents: searching ? "none" : "auto" }}>
              <span className="finder-save-icon"><FaPhone /></span>
              <input className="finder-save-input" type="tel" placeholder={lead.phone || "Phone / WhatsApp number…"}
                value={manualPhone} onChange={e => setManualPhone(e.target.value)} />
              <button className="finder-save-btn" disabled={!manualPhone.trim()}
                onClick={() => { updateLead(lead.id, { phone: manualPhone }); setManualPhone(""); }}>Save</button>
            </div>
            <div className="finder-save-row" style={{ pointerEvents: searching ? "none" : "auto" }}>
              <span className="finder-save-icon"><FaLinkedin /></span>
              <input className="finder-save-input" type="url" placeholder={lead.linkedin || "LinkedIn company URL…"}
                value={manualLinkedin} onChange={e => setManualLinkedin(e.target.value)} />
              <button className="finder-save-btn" disabled={!manualLinkedin.trim()}
                onClick={() => { updateLead(lead.id, { linkedin: manualLinkedin }); setManualLinkedin(""); }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── CHANNEL CARDS ─── */}
      <div className="channel-cards" style={{ pointerEvents: searching ? "none" : "auto" }}>
        {CHANNELS.map(ch => (
          <button
            key={ch.id}
            className={`channel-card ${selectedChannel === ch.id ? "cc-active" : ""} ${ch.ready ? "cc-ready" : "cc-draft"}`}
            onClick={() => setSelectedChannel(ch.id)}
          >
            <span className="cc-icon">{ch.icon}</span>
            <span className="cc-label">{ch.label}</span>
            <span className={`cc-badge ${ch.ready ? "cbb-ok" : "cbb-miss"}`}>{ch.ready ? "Ready" : "No data"}</span>
            <span className="cc-status">{ch.status}</span>
          </button>
        ))}
      </div>

      {/* ─── COMPOSER ─── */}
      <div className="outreach-composer-card" style={{ pointerEvents: searching ? "none" : "auto" }}>
        {gmailStatus.connected ? (
          <div className="gmail-status-bar gmail-connected" style={{ pointerEvents: searching ? "none" : "auto" }}>✅ Gmail: <strong>{gmailStatus.email}</strong></div>
        ) : (
          <div className="gmail-status-bar gmail-disconnected" style={{ pointerEvents: searching ? "none" : "auto" }}>🔗 <a href={`${API}/api/gmail/auth`} target="_blank" rel="noreferrer">Connect Gmail</a> to send emails</div>
        )}

        {requireEmailFirst ? (
          <>
            <div className="section-label" style={{ pointerEvents: searching ? "none" : "auto" }}>Priority</div>
            <div className="outreach-hint" style={{ marginBottom: 12 }}>
              Find or add an email first. After email is available, the message generator will unlock.
            </div>
            <button
              className="btn-outreach-generate"
              onClick={() => setFinderOpen(true)}
              disabled={outreachLoading}
            >
              📭 Find Email First
            </button>
          </>
        ) : (
          <>
            <div className="section-label" style={{ pointerEvents: searching ? "none" : "auto" }}>Tone</div>
            <div className="tone-buttons" style={{ pointerEvents: searching ? "none" : "auto" }}>
              {tones.map(t => (
                <button key={t} className={`tone-btn ${selectedTone === t ? "active" : ""}`} onClick={() => setSelectedTone(t)}>{t}</button>
              ))}
            </div>

            <button
              className="btn-outreach-generate"
              onClick={() => generateOutreach(lead, activeChannel.label, selectedTone)}
              disabled={outreachLoading}
            >
              {outreachLoading ? <span><FaSpinner className="spin" /> Generating…</span> : `✨ Generate ${selectedTone} ${activeChannel.label} Message`}
            </button>
          </>
        )}
      </div>

      {!requireEmailFirst && outreachLoading && <div className="loading-msg" style={{ pointerEvents: searching ? "none" : "auto" }}><FaSpinner className="spin" /> Generating...</div>}

      {outreachMsg && (
        <div className="outreach-result" style={{ pointerEvents: searching ? "none" : "auto" }}>
          {selectedChannel === "email" && (
            <div className="email-subject-row" style={{ pointerEvents: searching ? "none" : "auto" }}>
              <label className="email-subject-label">Subject</label>
              <input className="email-subject-input" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Email subject..." />
            </div>
          )}
          <textarea
            className="outreach-area"
            value={emailBody || outreachMsg}
            onChange={e => { setEmailBody(e.target.value); setOutreachMsg(e.target.value); }}
            rows={7}
          />
          <div className="outreach-actions" style={{ pointerEvents: searching ? "none" : "auto" }}>
            <button className="btn-ghost btn-sm" onClick={() => navigator.clipboard.writeText(emailBody || outreachMsg)}>Copy</button>
            <button className="btn-send-action" onClick={handleSend}>{sendLabel()}</button>
            <button className="btn-primary btn-sm" onClick={() => updateLead(lead.id, { status: "Contacted" })}>Mark Contacted</button>
          </div>
        </div>
      )}

      {!outreachMsg && !outreachLoading && (
        <div className="outreach-empty-state" style={{ pointerEvents: searching ? "none" : "auto" }}>
          <div className="outreach-hint" style={{ pointerEvents: searching ? "none" : "auto" }}>{hasEmail ? `Email ${primaryEmail} ready. Generate and send.` : inferredFromSource ? "Website inferred from source URL. Save it, then run contact extraction to get an email." : "No email yet. Open Find More and get email data first, then continue."}
          </div>
        </div>
      )}

      {draftText && (
        <div className="outreach-polish-card" style={{ pointerEvents: searching ? "none" : "auto" }}>
          <div className="outreach-polish-top" style={{ pointerEvents: searching ? "none" : "auto" }}>
            <div className="outreach-polish-title" style={{ pointerEvents: searching ? "none" : "auto" }}>Message Quality</div>
            <div className={`quality-pill quality-${qualityScore >= 75 ? "high" : qualityScore >= 50 ? "mid" : "low"}`}>{qualityScore}/100</div>
          </div>

          <div className="quality-grid" style={{ pointerEvents: searching ? "none" : "auto" }}>
            <div className={`quality-item ${hasName ? "ok" : "miss"}`}>{hasName ? "Yes" : "No"} Personalization</div>
            <div className={`quality-item ${hasCTA ? "ok" : "miss"}`}>{hasCTA ? "Yes" : "No"} Clear CTA</div>
            <div className={`quality-item ${hasService ? "ok" : "miss"}`}>{hasService ? "Yes" : "No"} Service mention</div>
            <div className={`quality-item ${isShortEnough ? "ok" : "miss"}`}>{isShortEnough ? "Yes" : "No"} Concise ({wordCount} words)</div>
          </div>

          <div className="section-label" style={{ pointerEvents: searching ? "none" : "auto" }}>Quick Enhancers</div>
          <div className="snippet-row" style={{ pointerEvents: searching ? "none" : "auto" }}>
            {!hasCTA && <button className="snippet-btn" onClick={() => addSnippet("Would you be open to a quick 15-minute chat this week?")}>Add CTA</button>}
            {!hasService && <button className="snippet-btn" onClick={() => addSnippet("I can help with website upgrades, UX improvements, and lead generation setup.")}>Add service fit</button>}
            {lead.websiteStatus === "missing" && <button className="snippet-btn" onClick={() => addSnippet("Since there is no website yet, we can launch a simple high-converting page quickly.")}>No-website angle</button>}
            <button className="snippet-btn" onClick={() => addSnippet("Happy to share a 2-step plan tailored to your business.")}>Add soft close</button>
          </div>
        </div>
      )}
    </div>
  );
}
